/**
 * <cc-product-extras> — product page rows under the main product:
 *   related        Shopify recommendations (intent=related); replaces the server-rendered same-collection fallback when results come back.
 *   complementary  Shopify recommendations (intent=complementary, set in Search & Discovery); stays hidden when empty.
 *   recent         last 4 products viewed in this browser (localStorage), excluding the current one; hidden when empty.
 *
 * Uses the documented Ajax endpoint `{routes.product_recommendations_url}.json`, so no section rendering is required.
 * Cards are cloned from the <template data-extras-card> authored in Liquid.
 */
const STORAGE_KEY = 'cc-recently-viewed';
const MAX_RECENT = 4; // spec: the last 4 products

class CcProductExtras extends HTMLElement {
  connectedCallback() {
    this.template = this.querySelector('template[data-extras-card]');
    this.money = new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang || 'fr', {
      style: 'currency',
      currency: this.dataset.currency || 'EUR',
    });
    this.#recent();
    this.#recommendations('related', 4);
    this.#recommendations('complementary', 4);
  }

  async #recommendations(intent, limit) {
    const section = this.querySelector(`[data-extras="${intent}"]`);
    if (!section || !this.dataset.recommendationsUrl) return;
    const url = new URL(`${this.dataset.recommendationsUrl}.json`, location.origin);
    url.searchParams.set('product_id', this.dataset.productId);
    url.searchParams.set('limit', String(limit));
    url.searchParams.set('intent', intent);
    try {
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const data = await response.json();
      const products = (data.products || []).filter((p) => p.available !== false).slice(0, limit);
      if (!products.length) return;
      this.#fill(
        section,
        products.map((p) => ({
          url: p.url,
          title: p.title,
          image: p.featured_image ? this.#sized(p.featured_image, 600) : '',
          price: this.money.format(p.price / 100),
        }))
      );
    } catch {
      /* keep the fallback (related) or the hidden state (complementary) */
    }
  }

  #recent() {
    let list = [];
    try {
      list = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    } catch {}
    let current = null;
    try {
      current = JSON.parse(this.dataset.current || 'null');
    } catch {}
    const others = list.filter((item) => item && item.handle !== current?.handle).slice(0, MAX_RECENT);
    const section = this.querySelector('[data-extras="recent"]');
    if (section && others.length) this.#fill(section, others);
    if (current) {
      const next = [current, ...list.filter((item) => item && item.handle !== current.handle)].slice(0, MAX_RECENT + 1);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {}
    }
  }

  #fill(section, items) {
    const list = section.querySelector('[data-extras-list]');
    if (!list || !this.template) return;
    const fragment = document.createDocumentFragment();
    for (const item of items) {
      const node = this.template.content.cloneNode(true);
      const link = node.querySelector('[data-card-link]');
      link.href = item.url;
      node.querySelector('[data-card-title]').textContent = item.title;
      node.querySelector('[data-card-price]').textContent = item.price;
      const img = node.querySelector('[data-card-image]');
      if (item.image) {
        img.src = item.image;
        img.alt = item.title;
      } else {
        img.remove();
      }
      fragment.append(node);
    }
    list.replaceChildren(fragment);
    section.hidden = false;
  }

  #sized(src, width) {
    try {
      const url = new URL(src.startsWith('//') ? `https:${src}` : src, location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch {
      return src;
    }
  }
}

if (!customElements.get('cc-product-extras')) customElements.define('cc-product-extras', CcProductExtras);

/* AVIS GOOGLE (Entangle Google Reviews) : ajoute sous la fiche produit une section avec les avis de la fiche Google
   CharlyCorp et un bouton pour laisser un avis. Insérée juste avant <cc-product-extras> (Vous aimerez aussi). */
const CC_GOOGLE_REVIEW_URL = 'https://g.page/r/CcVQfDmXVVoFEBM/review';
function ccInjectGoogleReviews() {
  if (document.getElementById('avis-google')) return;
  const anchor = document.querySelector('cc-product-extras');
  if (!anchor || !anchor.parentNode) return;
  const style = document.createElement('style');
  style.textContent = `
    #avis-google{border-top:1px solid var(--color-border,rgba(255,255,255,.12));padding:60px 0}
    #avis-google .cc-g-top{display:flex;flex-wrap:wrap;align-items:flex-end;justify-content:space-between;gap:24px;margin-bottom:28px}
    #avis-google .cc-g-eyebrow{font-size:12px;font-weight:600;letter-spacing:.2em;text-transform:uppercase;margin:0 0 12px}
    #avis-google .cc-g-head{font-size:clamp(32px,5vw,56px);line-height:1;text-transform:uppercase;font-weight:400;margin:0}
    #avis-google .cc-g-btn{display:inline-flex;align-items:center;gap:.75rem;height:56px;padding:0 32px;font-size:13px;letter-spacing:.16em;text-transform:uppercase;text-decoration:none;white-space:nowrap}
    #avis-google .cc-g-box{background:#fff;border-radius:26px;padding:24px}
    #avis-google .cc-g-note{margin-top:16px;font-size:14px;opacity:.7}
    @media (min-width:768px){#avis-google .cc-g-box{padding:40px}}`;
  const section = document.createElement('section');
  section.id = 'avis-google';
  section.setAttribute('aria-labelledby', 'google-reviews-heading');
  section.innerHTML = `
    <div class="cc-g-top">
      <div>
        <p class="cc-g-eyebrow text-accent">Avis Google</p>
        <h2 id="google-reviews-heading" class="cc-g-head font-heading">Ils parlent de CharlyCorp</h2>
      </div>
      <a href="${CC_GOOGLE_REVIEW_URL}" target="_blank" rel="noopener" class="cc-g-btn button-primary rounded-button">&#9733; Laisser un avis Google <span aria-hidden="true">&rarr;</span></a>
    </div>
    <div class="cc-g-box"><div id="grp-entangle--root" class="grp-entangle--root"></div></div>
    <p class="cc-g-note">Déjà client ? Votre avis aide d'autres personnes à découvrir CharlyCorp. Merci !</p>`;
  anchor.parentNode.insertBefore(style, anchor);
  anchor.parentNode.insertBefore(section, anchor);
  if (!document.querySelector('link[href*="gr-extension.min.css"]')) {
    const css = document.createElement('link');
    css.rel = 'stylesheet';
    css.href = 'https://assets.entanglecommerce.com/google-review-assets/gr-extension.min.css';
    document.head.append(css);
  }
  if (!document.querySelector('script[src*="gr-extension.min.js"]')) {
    const js = document.createElement('script');
    js.setAttribute('shopify_url', 'f9ugfn-fa.myshopify.com');
    js.setAttribute('shopify_data', JSON.stringify({address1:'154 Route de Mittelhausbergen',phone:'0771868252',city:'Strasbourg',zip:'67200',country:'France',company:'CharlyCorp',country_code:'FR'}));
    js.defer = true;
    js.src = 'https://assets.entanglecommerce.com/google-review-assets/gr-extension.min.js';
    document.body.append(js);
  }
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ccInjectGoogleReviews);
else ccInjectGoogleReviews();
